import React, { useEffect, useState } from 'react';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import _ from 'lodash';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, IconButton, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, Tooltip, Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../../../layouts/Dashboard';
import { formatIsoTimestamp, getPushActionLabel, getPushStatusChip } from '../../../../utils/mappers';
import { mobilizeiPushNotificationsService } from '../../../../services/mobilizei/pushNotifications.service';
import { useUser } from '../../../../contexts/user.context';
import { showApiError } from '../../../../utils/errors';

const PAGE_SIZE = 20;

export default function PushList() {
    const { user } = useUser();
    const navigate = useNavigate();

    const platformId = user?.store?.platformId;
    const storeId = user?.store?.id;

    const [isLoading, setIsLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);

    const [filter, setFilter] = useState('all');
    const [filteredNotifications, setFilteredNotifications] = useState([]);
    const [filteredTotalCount, setFilteredTotalCount] = useState(0);

    const [confirmAction, setConfirmAction] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        getNotifications(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const getNotifications = (pageToLoad) => {
        if (!platformId || !storeId) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        mobilizeiPushNotificationsService.getAllPushNotifications(platformId, storeId, pageToLoad)
            .then((response) => {
                setNotifications(response.notifications);
                setFilteredNotifications(response.notifications);
                setTotalCount(response.totalCount);
                setFilteredTotalCount(response.totalCount);
            })
            .catch((e) => {
                showApiError(e?.error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const getNotificationName = (notification) => {
        const name = notification.title || notification.text || '';
        return name.length > 40 ? `${name.slice(0, 40)}...` : name;
    }

    const getNotificationDate = (notification, showSent) => {
        if (showSent) {
            if (notification.status === 'scheduled') {
                return formatIsoTimestamp(notification.scheduledFor, 'DD/MM/YYYY HH:mm');
            }
            return formatIsoTimestamp(notification.sentAt, 'DD/MM/YYYY HH:mm');
        }

        return formatIsoTimestamp(notification.createdAt, 'DD/MM/YYYY HH:mm');
    }

    const handleDuplicate = (notification) => {
        navigate('/push/new', { state: { duplicateFrom: notification } });
    }

    const handleEdit = (notification) => {
        navigate('/push/new', { state: { duplicateFrom: notification, isEditing: true } });
    }

    const handleFilteredNotifications = (filter) => {
        setFilter(filter);
        if (filter === 'all') {
            setFilteredNotifications(notifications);
            setFilteredTotalCount(totalCount);
        } else {
            const filteredNotifications = notifications.filter((n) => n.status === filter);
            setFilteredNotifications(filteredNotifications);
            setFilteredTotalCount(filteredNotifications.length);
        }
    }

    const handleConfirm = () => {
        if (!confirmAction) return;

        const { type, notification } = confirmAction;

        const request = type === 'cancel'
            ? mobilizeiPushNotificationsService.cancelPushNotification(platformId, storeId, notification.id)
            : mobilizeiPushNotificationsService.deletePushNotification(platformId, storeId, notification.id);

        setIsProcessing(true);

        request
            .then(() => {
                toast.success(type === 'cancel'
                    ? 'Notificação cancelada com sucesso!'
                    : 'Notificação excluída com sucesso!');
                setConfirmAction(null);
                getNotifications(page);
            })
            .catch((e) => {
                showApiError(e?.error);
            })
            .finally(() => {
                setIsProcessing(false);
            });
    }

    const canCancel = (notification) => notification.status === 'scheduled';
    const canDelete = (notification) => ['sent', 'canceled', 'failed'].includes(notification.status);

    return (
        <DashboardLayout
            Icon={SendIcon}
            title="Notificações push"
            subtitle="Acompanhe todas as notificações enviadas e agendadas do seu aplicativo"
        >
            {!_.isEmpty(notifications) && (
                <Box display="flex" justifyContent="space-between" mb={2} mt={2}>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        <Button
                            variant={filter === 'all' ? 'contained' : 'outlined'}
                            onClick={() => handleFilteredNotifications('all')}
                            style={{ textTransform: 'lowercase' }}
                        >
                            Todas
                        </Button>
                        <Button
                            variant={filter === 'sent' ? 'contained' : 'outlined'}
                            onClick={() => handleFilteredNotifications('sent')}
                            style={{ textTransform: 'lowercase' }}
                        >
                            Enviadas
                        </Button>
                        <Button
                            variant={filter === 'scheduled' ? 'contained' : 'outlined'}
                            onClick={() => handleFilteredNotifications('scheduled')}
                            style={{ textTransform: 'lowercase' }}
                        >
                            Agendadas
                        </Button>
                        <Button
                            variant={filter === 'canceled' ? 'contained' : 'outlined'}
                            onClick={() => handleFilteredNotifications('canceled')}
                            style={{ textTransform: 'lowercase' }}
                        >
                            Canceladas
                        </Button>
                    </Box>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/push/new')}
                    >
                        Criar notificação
                    </Button>
                </Box>
            )}

            {isLoading ? (
                <Box display="flex" alignItems="center" justifyContent="center" mt={2} mb={6}>
                    <CircularProgress />
                </Box>
            ) : _.isEmpty(notifications) ? (
                <Box textAlign="center" mt={4} mb={6}>
                    <NotificationsNoneIcon />
                    <Typography variant="body1" align="center" mb={2}>
                        Você ainda não enviou nenhuma notificação.
                    </Typography>

                    <Button
                        variant="contained"
                        onClick={() => navigate('/push/new')}
                    >
                        Criar notificação
                    </Button>
                </Box>
            ) : (
                <Box mb={6}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nome</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                    <TableCell align="center">Envio em</TableCell>
                                    <TableCell align="center">Criado em</TableCell>
                                    <TableCell align="center"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredNotifications.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            Você {filter === 'scheduled' && 'ainda'} não possui notificações push {filter === 'scheduled' ? 'agendadas' : filter === 'canceled' ? 'canceladas' : 'enviadas'}.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {filteredNotifications.map((notification) => (
                                    <TableRow key={notification.id}>
                                        <TableCell>{getNotificationName(notification) || '-'}</TableCell>
                                        <TableCell align="center">{getPushStatusChip(notification.status)}</TableCell>
                                        <TableCell align="center">{getNotificationDate(notification, true) || '-'}</TableCell>
                                        <TableCell align="center">{getNotificationDate(notification, false) || '-'}</TableCell>
                                        <TableCell align="right">
                                            <Box display="flex" flexWrap="nowrap" justifyContent="flex-end">
                                                {canCancel(notification) ? (
                                                    <Tooltip title="Editar">
                                                        <IconButton onClick={() => handleEdit(notification)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Ver">
                                                        <IconButton onClick={() => navigate(`/push/${notification.id}`)}>
                                                            <VisibilityIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                <Tooltip title="Duplicar">
                                                    <IconButton onClick={() => handleDuplicate(notification)}>
                                                        <ContentCopyIcon />
                                                    </IconButton>
                                                </Tooltip>

                                                {canCancel(notification) && (
                                                    <Tooltip title="Cancelar">
                                                        <IconButton onClick={() => setConfirmAction({ type: 'cancel', notification })}>
                                                            <CancelIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {canDelete(notification) && (
                                                    <Tooltip title="Excluir">
                                                        <IconButton onClick={() => setConfirmAction({ type: 'delete', notification })}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={filteredTotalCount}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={PAGE_SIZE}
                        rowsPerPageOptions={[PAGE_SIZE]}
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            <Dialog open={!!confirmAction} onClose={() => !isProcessing && setConfirmAction(null)}>
                <DialogTitle>
                    {confirmAction?.type === 'cancel' ? 'Cancelar notificação' : 'Excluir notificação'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmAction?.type === 'cancel'
                            ? 'Tem certeza que deseja cancelar o envio desta notificação agendada?'
                            : 'Ao excluir esta notificação, ela será removida permanentemente do histórico. Deseja continuar?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmAction(null)} disabled={isProcessing} variant='outlined'>
                        Voltar
                    </Button>
                    {isProcessing ? (
                        <Box display="flex" alignItems="center" px={2}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <Button onClick={handleConfirm} variant="outlined" color="error">
                            {confirmAction?.type === 'cancel' ? 'Cancelar envio' : 'Excluir'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}
